import { useState, useRef, useEffect } from 'react';
import {
  Box,
  CardMedia,
  Dialog,
  IconButton,
  Typography,
  Chip,
  Fade,
  Zoom,
  Tooltip,
  ButtonGroup,
  Button
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import {
  ChevronLeft,
  ChevronRight,
  Close,
  PlayArrow,
  Image as ImageIcon,
  ZoomIn,
  ZoomOut,
  Fullscreen,
  FitScreen,
  CenterFocusStrong
} from '@mui/icons-material';

interface MediaItem {
  key: string;
  mediaType: string;
}

interface ShowcaseMediaDisplayProps {
  media: MediaItem[];
  height?: number | string;
  showMediaCount?: boolean;
  clickable?: boolean;
  borderRadius?: number;
}

const ShowcaseMediaDisplay: React.FC<ShowcaseMediaDisplayProps> = ({
  media,
  height = 200,
  showMediaCount = true,
  clickable = true,
  borderRadius = 1
}) => {
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [viewMode, setViewMode] = useState<'contain' | 'cover'>('contain');
  const [isZoomed, setIsZoomed] = useState(false);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleKeyPress = (event: KeyboardEvent) => {
    if (!isDialogOpen) return;

    switch (event.key) {
      case 'ArrowLeft':
        if (media.length > 1) handlePrevious();
        break;
      case 'ArrowRight':
        if (media.length > 1) handleNext();
        break;
      case 'Escape':
        setIsDialogOpen(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isDialogOpen) {
      const hideControls = () => {
        if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = setTimeout(() => {
          setControlsVisible(false);
        }, 3000);
      };
      hideControls();
      return () => {
        if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
      };
    }
  }, [isDialogOpen, selectedMediaIndex]);

  useEffect(() => {
    if (isDialogOpen) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialogOpen, media.length]);

  if (!media || media.length === 0) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          color: 'text.secondary',
          borderRadius,
          border: '2px dashed',
          borderColor: 'grey.300'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <ImageIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="caption" display="block">
            No media available
          </Typography>
        </Box>
      </Box>
    );
  }

  const primaryMedia = media[0];
  const hasMultipleMedia = media.length > 1;

  const handleMediaClick = () => {
    console.log('Media clicked - clickable:', clickable);
    if (clickable) {
      console.log('Opening dialog');
      setIsDialogOpen(true);
      setSelectedMediaIndex(0);
    }
  };

  const handlePrevious = () => {
    setSelectedMediaIndex(prev =>
      prev === 0 ? media.length - 1 : prev - 1
    );
    resetZoom();
  };

  const handleNext = () => {
    setSelectedMediaIndex(prev =>
      prev === media.length - 1 ? 0 : prev + 1
    );
    resetZoom();
  };

  const handleMouseMove = () => {
    setControlsVisible(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      setControlsVisible(false);
    }, 3000);
  };

  const toggleViewMode = () => {
    setViewMode(prev => prev === 'contain' ? 'cover' : 'contain');
  };

  const resetZoom = () => {
    setIsZoomed(false);
  };

  const renderMedia = (mediaItem: MediaItem, isPreview: boolean = true, customStyles: SxProps<Theme> = {}) => {
    const mediaUrl = `${import.meta.env.VITE_R2_ENDPOINT}/${mediaItem.key}`;

    const baseStyles: SxProps<Theme> = {
      cursor: clickable && isPreview ? 'pointer' : 'default',
      borderRadius: isPreview ? borderRadius : 0,
      transition: 'all 0.3s ease-in-out',
      ...customStyles as Record<string, unknown>
    };

    if (mediaItem.mediaType === 'video') {
      return (
        <CardMedia
          component="video"
          height={isPreview ? height : 'auto'}
          controls={!isPreview}
          src={mediaUrl}
          sx={{
            objectFit: isPreview ? 'cover' : viewMode,
            maxHeight: isPreview ? height : '85vh',
            maxWidth: isPreview ? '100%' : '90vw',
            width: isPreview ? '100%' : 'auto',
            ...baseStyles as Record<string, unknown>
          }}
          onClick={isPreview ? handleMediaClick : undefined}
        />
      );
    } else {
      return (
        <CardMedia
          component="img"
          height={isPreview ? height : 'auto'}
          image={mediaUrl}
          alt={`Media ${selectedMediaIndex + 1}`}
          sx={{
            objectFit: isPreview ? 'cover' : viewMode,
            maxHeight: isPreview ? height : '85vh',
            maxWidth: isPreview ? '100%' : '90vw',
            width: isPreview ? '100%' : 'auto',
            transform: !isPreview && isZoomed ? 'scale(1.5)' : 'scale(1)',
            cursor: !isPreview ? 'pointer' : (clickable ? 'pointer' : 'default'),
            ...baseStyles as Record<string, unknown>
          }}
          onClick={isPreview ? handleMediaClick : () => setIsZoomed(!isZoomed)}
        />
      );
    }
  };

  return (
    <>
      {/* Primary Media Display */}
      <Box sx={{ position: 'relative', borderRadius, overflow: 'hidden' }}>
        {renderMedia(primaryMedia)}

        {/* Media Count Badge */}
        {hasMultipleMedia && showMediaCount && (
          <Chip
            size="small"
            label={`${media.length}`}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              bgcolor: 'rgba(0,0,0,0.8)',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              '& .MuiChip-icon': {
                color: 'white',
                fontSize: '1rem'
              }
            }}
            icon={
              primaryMedia.mediaType === 'video' ?
                <PlayArrow /> :
                <ImageIcon />
            }
          />
        )}

        {/* Preview Overlay for Multiple Media */}
        {hasMultipleMedia && clickable && (
          <Fade in timeout={300}>
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                color: 'white',
                p: 2,
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                Click to view all {media.length} items
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Preview Hover Overlay */}
        {clickable && (
          <Box
            onClick={handleMediaClick}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.3s ease-in-out',
              cursor: 'pointer',
              '&:hover': {
                opacity: 1,
                bgcolor: 'rgba(0,0,0,0.3)'
              }
            }}
          >
            <IconButton
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                console.log('Enlarge image clicked');
                e.stopPropagation();
                handleMediaClick();
              }}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': { bgcolor: 'grey.100' }
              }}
            >
              <Fullscreen />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Enhanced Full Media Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth={false}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0,0,0,0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }
        }}
        TransitionComponent={Zoom}
        transitionDuration={300}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseMove={handleMouseMove}
        >
          {/* Top Controls Bar */}
          <Fade in={controlsVisible} timeout={300}>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(rgba(0,0,0,0.7), transparent)',
                zIndex: 2,
                p: 2
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {/* Media Info */}
                <Box sx={{ color: 'white' }}>
                  {hasMultipleMedia && (
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {selectedMediaIndex + 1} of {media.length}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {media[selectedMediaIndex].mediaType === 'video' ? 'Video' : 'Image'}
                  </Typography>
                </Box>

                {/* View Controls */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {media[selectedMediaIndex].mediaType === 'image' && (
                    <>
                      <ButtonGroup variant="contained" size="small">
                        <Button
                          onClick={toggleViewMode}
                          startIcon={viewMode === 'contain' ? <CenterFocusStrong /> : <FitScreen />}
                          sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                        >
                          {viewMode === 'contain' ? 'Fit' : 'Fill'}
                        </Button>
                        <Button
                          onClick={() => setIsZoomed(!isZoomed)}
                          startIcon={isZoomed ? <ZoomOut /> : <ZoomIn />}
                          sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                        >
                          {isZoomed ? 'Zoom Out' : 'Zoom In'}
                        </Button>
                      </ButtonGroup>
                    </>
                  )}

                  <Tooltip title="Close (Esc)">
                    <IconButton
                      onClick={() => setIsDialogOpen(false)}
                      sx={{
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.1)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                      }}
                    >
                      <Close />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </Fade>

          {/* Navigation Arrows */}
          {hasMultipleMedia && (
            <>
              <Fade in={controlsVisible} timeout={300}>
                <Tooltip title="Previous (left arrow)">
                  <IconButton
                    onClick={handlePrevious}
                    sx={{
                      position: 'absolute',
                      left: 24,
                      color: 'white',
                      bgcolor: 'rgba(0,0,0,0.5)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                      zIndex: 2,
                      width: 56,
                      height: 56
                    }}
                  >
                    <ChevronLeft sx={{ fontSize: 32 }} />
                  </IconButton>
                </Tooltip>
              </Fade>

              <Fade in={controlsVisible} timeout={300}>
                <Tooltip title="Next (right arrow)">
                  <IconButton
                    onClick={handleNext}
                    sx={{
                      position: 'absolute',
                      right: 24,
                      color: 'white',
                      bgcolor: 'rgba(0,0,0,0.5)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                      zIndex: 2,
                      width: 56,
                      height: 56
                    }}
                  >
                    <ChevronRight sx={{ fontSize: 32 }} />
                  </IconButton>
                </Tooltip>
              </Fade>
            </>
          )}

          {/* Main Media Display */}
          <Box sx={{ maxWidth: '90vw', maxHeight: '85vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {renderMedia(media[selectedMediaIndex], false)}
          </Box>

          {/* Bottom Thumbnail Strip */}
          {hasMultipleMedia && (
            <Fade in={controlsVisible} timeout={300}>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 24,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 1,
                  maxWidth: '80vw',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  bgcolor: 'rgba(0,0,0,0.3)',
                  borderRadius: 3,
                  p: 2,
                  backdropFilter: 'blur(10px)',
                  '&::-webkit-scrollbar': {
                    height: 4
                  },
                  '&::-webkit-scrollbar-track': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderRadius: 2
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                    borderRadius: 2
                  }
                }}
              >
                {media.map((mediaItem, index) => (
                  <Box
                    key={index}
                    onClick={() => {
                      setSelectedMediaIndex(index);
                      resetZoom();
                    }}
                    sx={{
                      width: 80,
                      height: 60,
                      cursor: 'pointer',
                      border: selectedMediaIndex === index ? '3px solid white' : '2px solid transparent',
                      borderRadius: 2,
                      overflow: 'hidden',
                      flexShrink: 0,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        border: '2px solid rgba(255,255,255,0.8)'
                      }
                    }}
                  >
                    {mediaItem.mediaType === 'video' ? (
                      <video
                        src={`${import.meta.env.VITE_R2_ENDPOINT}/${mediaItem.key}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <img
                        src={`${import.meta.env.VITE_R2_ENDPOINT}/${mediaItem.key}`}
                        alt={`Thumbnail ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    )}
                    {/* Thumbnail overlay for selected */}
                    {selectedMediaIndex === index && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: 'rgba(255,255,255,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'white'
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Fade>
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default ShowcaseMediaDisplay;